import React from 'react';
import { Sparkles, ArrowRight, Play, Code2, Heart } from 'lucide-react';
import { Button } from './ui/button';

const LandingHero = ({ onStartAnimating }) => {
  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col selection:bg-violet-500/30">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-20 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-violet-600/10 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] animate-pulse delay-700" />
        </div>

        {/* Floating Shapes */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-20 left-[10%] w-32 h-32 border-2 border-violet-500/30 rounded-2xl rotate-12 animate-bounce shadow-2xl shadow-violet-500/20" />
          <div className="absolute bottom-40 right-[15%] w-24 h-24 bg-indigo-500/20 rounded-xl -rotate-12 animate-pulse" />
          <div className="absolute top-1/2 right-[10%] w-16 h-16 border border-violet-400/20 rounded-lg rotate-45" />
        </div>

        <div className="relative z-10 text-center max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl mb-8 backdrop-blur-md shadow-xl">
            <Sparkles className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-bold text-slate-300 uppercase tracking-widest">Premium Motion Engine</span>
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-black mb-8 leading-[1] md:leading-[0.9] tracking-tighter">
            <span className="text-white uppercase">
              Animations
            </span>
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-indigo-400 to-violet-500 bg-clip-text text-transparent uppercase">
              Beyond Limits
            </span>
          </h1>

          {/* Description */}
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-12 font-medium leading-relaxed">
            Craft stunning motion experiences with <span className="text-violet-300 font-bold">I ❤️ Animation</span>.
            The most powerful CSS orchestration tool for modern developers and designers.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button
              onClick={onStartAnimating}
              size="lg"
              className="bg-gradient-to-br from-violet-500 to-indigo-600 hover:from-violet-400 hover:to-indigo-500 text-white font-black px-10 py-8 text-xl rounded-2xl shadow-[0_20px_50px_-12px_rgba(139,92,246,0.5)] hover:shadow-violet-500/60 transition-all duration-500 group relative overflow-hidden active:scale-95"
            >
              <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              <Play className="w-5 h-5 mr-3 group-hover:rotate-12 transition-transform relative z-10" />
              <span className="relative z-10">Start Animating</span>
              <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-2 transition-transform relative z-10" />
            </Button>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 mt-24 max-w-5xl w-full px-4">
          <FeatureCard
            icon={<Play className="w-6 h-6" />}
            title="Real-time Engine"
            description="Precision-engineered live previews with frame-accurate orchestration."
          />
          <FeatureCard
            icon={<Code2 className="w-6 h-6" />}
            title="Universal Export"
            description="Seamlessly bridge into SwiftUI, Flutter, React, and pure CSS."
          />
          <FeatureCard
            icon={<Heart className="w-6 h-6" />}
            title="Smart Library"
            description="Save your favorites and build a custom motion design system."
          />
        </div>
      </div>

      {/* Footer */}
      <footer className="py-10 text-center border-t border-white/5 opacity-60">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-[0.3em]">
          Empowering Creators with I ❤️ Animation
        </p>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="p-8 bg-white/[0.02] border border-white/5 rounded-[2rem] backdrop-blur-xl hover:bg-white/[0.05] hover:border-violet-500/30 transition-all duration-500 group cursor-default">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/20 to-indigo-500/20 flex items-center justify-center text-violet-400 mb-6 group-hover:rotate-6 group-hover:scale-110 transition-all duration-500 shadow-lg">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-white mb-3 tracking-tight">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed font-medium">{description}</p>
  </div>
);

export default LandingHero;

